import {
	ActionIcon,
	Badge,
	Button,
	Card,
	Container,
	Group,
	LoadingOverlay,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { EdgeDetailDrawer } from "../components/EdgeDetailDrawer";
import { LoadingState } from "../components/LoadingState";
import { NodeDetailSidebar } from "../components/NodeDetailSidebar";
import { PropertyTypeIcon } from "../components/ViewEditNodeModal/PropertyTypeIcon";
import { PropertyValue } from "../components/ViewEditNodeModal/PropertyValue";
import { inferPropertyType } from "../components/ViewEditNodeModal/types";
import { VirtualizedCardList } from "../components/Workflows/shared/VirtualizedCardList";
import { useViewState } from "../contexts/ViewStateContext";
import {
	type Edge as GraphEdge,
	type Node as GraphNode,
	useGetCurrentUserAndOrganizationsQuery,
} from "../graphql/generated/types.gen";
import {
	useActiveOrganization,
	useActiveOrganizationRequired,
} from "../hooks/useActiveOrganization";
import { useAuthCompat } from "../hooks/useAuthCompat";
import { useGraphApi } from "../hooks/useGraphApi";
import type { RestGraphEdge } from "../types/graph";

export const Route = createFileRoute("/nodes")({
	component: NodesPage,
});

// Gate mirroring routes/index.tsx: useActiveOrganizationRequired throws until
// the current-user query resolves, so a direct deep-link to /nodes crashed to
// the error boundary before this loading check existed.
function NodesPage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const { data: userOrganizationData } = useGetCurrentUserAndOrganizationsQuery(
		{
			skip: !user,
		},
	);
	const { isReady } = useActiveOrganization();

	// Redirect to welcome if not authenticated
	useEffect(() => {
		if (!authLoading && (!user || !userOrganizationData?.currentUser)) {
			window.location.href = "/welcome";
		}
	}, [user, userOrganizationData, authLoading]);

	if (!isReady) {
		// Redirect (handled by the effect) rather than a loader that never resolves.
		if (!authLoading && (!user || !userOrganizationData?.currentUser)) {
			return null;
		}
		return <LoadingState />;
	}

	return <NodesPageContent />;
}

function NodesPageContent() {
	const [nodes, setNodes] = useState<GraphNode[]>([]);
	const [_edges, _setEdges] = useState<RestGraphEdge[]>([]);
	const [loading, setLoading] = useState(true);
	// Panel state is shared so the edge timeline can push over it and retarget it.
	const { detailNodeId, openNodePanel, resetDetailPanel } = useViewState();

	// The panel is route-scoped; a target left behind would re-open a panel on
	// whichever route is visited next.
	useEffect(() => resetDetailPanel, [resetDetailPanel]);
	const { queryGraphData, bulkGraphOperations } = useGraphApi();
	const { activeOrganization } = useActiveOrganizationRequired();

	const loadNodes = useCallback(async () => {
		if (!activeOrganization?.id) return;

		setLoading(true);
		try {
			const result = await queryGraphData({
				organizationId: activeOrganization.id,
			});
			// Add empty edges array to each node to match GraphNode type
			const nodesWithEdges = (result?.nodes || []).map((node) => ({
				...node,
				edges: [] as GraphEdge[],
			}));
			setNodes(nodesWithEdges);
			_setEdges(result?.edges || []);
		} catch (error) {
			console.error("Failed to load nodes:", error);
		} finally {
			setLoading(false);
		}
	}, [queryGraphData, activeOrganization.id]);

	useEffect(() => {
		loadNodes();
	}, [loadNodes]);

	// Derived from the shared panel target rather than stored separately: the
	// edge timeline can retarget the panel (clicking an endpoint), and local
	// state would keep rendering the node the user originally opened.
	// Map-based endpoint lookups for the same reason as buildEditNode: per-edge
	// linear finds are quadratic on hub nodes at production scale.
	const editingNode = useMemo<GraphNode | null>(() => {
		if (!detailNodeId) return null;
		const nodeById = new Map(nodes.map((n) => [n.id, n]));
		const node = nodeById.get(detailNodeId);
		if (!node) return null;

		const edgesWithNodes = _edges
			.filter(
				(edge) =>
					edge.sourceNode === detailNodeId || edge.targetNode === detailNodeId,
			)
			.map((edge) => ({
				...edge,
				source: nodeById.get(edge.sourceNode) || null,
				target: nodeById.get(edge.targetNode) || null,
			})) as GraphEdge[];

		return { ...node, edges: edgesWithNodes };
	}, [detailNodeId, nodes, _edges]);

	// Stable so the memoized NodeCard rows skip re-rendering on unrelated
	// state changes (loading flips, panel opens).
	const handleDeleteNode = useCallback(
		async (nodeId: string) => {
			if (
				!confirm(
					"Are you sure you want to delete this node? This action cannot be undone.",
				)
			) {
				return;
			}

			if (!activeOrganization?.id) return;

			try {
				await bulkGraphOperations({
					organizationId: activeOrganization.id,
					nodeIdsToDelete: [nodeId],
				});
				await loadNodes(); // Refresh the list
			} catch (error) {
				console.error("Failed to delete node:", error);
			}
		},
		[activeOrganization?.id, bulkGraphOperations, loadNodes],
	);

	const handleModalClose = () => {
		resetDetailPanel();
		loadNodes(); // Refresh the list after editing
	};

	return (
		<Container
			size="lg"
			py="xl"
			// Bounded height is what lets the card list virtualize: with the page
			// scrolling the document instead, the virtualizer's scroll element
			// grows to fit every row and all 10k cards mount (see
			// VirtualizedCardList). --header-height is kept current by RootLayout.
			style={{
				height: "calc(100dvh - var(--header-height))",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Stack gap="xl" style={{ flex: 1, minHeight: 0 }}>
				<Group justify="space-between">
					<Title order={1}>Directory</Title>
					<Button leftSection={<IconPlus size={16} />} onClick={() => {}}>
						Add Node
					</Button>
				</Group>

				<div
					style={{
						position: "relative",
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<LoadingOverlay visible={loading} />

					{nodes.length === 0 && !loading ? (
						<Card p="xl">
							<Text ta="center" c="dimmed">
								No nodes found. Create your first node to get started.
							</Text>
						</Card>
					) : (
						<VirtualizedCardList
							items={nodes}
							getKey={(node) => node.id}
							estimateSize={132}
							gap={16}
							renderItem={(node) => (
								<NodeCard
									node={node}
									onEdit={openNodePanel}
									onDelete={handleDeleteNode}
								/>
							)}
						/>
					)}
				</div>
			</Stack>

			{editingNode && (
				<NodeDetailSidebar
					node={editingNode}
					opened={detailNodeId !== null}
					onClose={handleModalClose}
					organizationId={activeOrganization.id}
					onSave={() => {
						handleModalClose();
					}}
					onDelete={() => {
						handleModalClose();
					}}
				/>
			)}

			{/* Entry points outside the node panel (none on this route today) land
			    here rather than in the panel's pushed view. */}
			<EdgeDetailDrawer
				organizationId={activeOrganization.id}
				onRefresh={loadNodes}
			/>
		</Container>
	);
}

interface NodeCardProps {
	node: GraphNode;
	onEdit: (nodeId: string) => void;
	onDelete: (nodeId: string) => void;
}

// Memoized: only the visible window mounts, but rows still re-render whenever
// the route's state changes unless their props are stable.
const NodeCard = memo(function NodeCard({
	node,
	onEdit,
	onDelete,
}: NodeCardProps) {
	return (
		<Card shadow="sm" padding="lg" radius="md" withBorder>
			<Group justify="space-between" align="flex-start">
				<Group align="flex-start" gap="md" style={{ flex: 1 }}>
					<Stack gap="xs" style={{ flex: 1 }}>
						<Group justify="space-between">
							<Group gap="sm">
								<Badge variant="light" size="sm">
									{node.typeName || "Unknown"}
								</Badge>
								<Text fw={500} size="lg">
									{node.displayName}
								</Text>
							</Group>
							<Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
								{node.id.slice(0, 8)}...
							</Text>
						</Group>

						{node.properties && Object.keys(node.properties).length > 0 && (
							<Group gap="lg">
								<Text size="sm" fw={500}>
									Properties:
								</Text>
								<Group gap="md">
									{Object.entries(node.properties)
										.slice(0, 3)
										.map(([key, value]) => (
											<Group key={key} gap="xs">
												<PropertyTypeIcon
													type={inferPropertyType(key, value)}
													value={value}
													size={14}
												/>
												<Text size="sm" c="dimmed">
													{key}:
												</Text>
												<PropertyValue
													value={value}
													type={inferPropertyType(key, value)}
													propertyKey={key}
													nodeName={node.displayName}
												/>
											</Group>
										))}
									{Object.keys(node.properties).length > 3 && (
										<Text size="sm" c="dimmed">
											+{Object.keys(node.properties).length - 3} more
										</Text>
									)}
								</Group>
							</Group>
						)}

						<Text size="xs" c="dimmed">
							Created: {new Date(node.createdAt).toLocaleDateString()}
						</Text>
					</Stack>
				</Group>

				<Group gap="xs">
					<ActionIcon
						aria-label="Edit node"
						variant="subtle"
						color="blue"
						onClick={() => onEdit(node.id)}
					>
						<IconEdit size={16} />
					</ActionIcon>
					<ActionIcon
						aria-label="Delete node"
						variant="subtle"
						color="red"
						onClick={() => onDelete(node.id)}
					>
						<IconTrash size={16} />
					</ActionIcon>
				</Group>
			</Group>
		</Card>
	);
});
